import { useCallback, useEffect, useState } from 'react';
import { meAPI } from '../services/api';

/**
 * Ποιες επιλογές του μενού (πάνω δεξιά) εμφανίζονται κατευθείαν· οι υπόλοιπες
 * είναι στο «Περισσότερα». `null` = ο χρήστης δεν έχει διαλέξει → προεπιλογές.
 *
 * Πηγή αλήθειας για υπαλλήλους: employees.preferences.menu_pins στον server,
 * ώστε η επιλογή να ακολουθεί τον υπάλληλο σε όποιο ταμείο κι αν συνδεθεί.
 * Αντίγραφο στο localStorage για άμεση εμφάνιση χωρίς αναβόσβημα — το Navigation
 * ξαναστήνεται σε κάθε αλλαγή σελίδας. Χρήστες κεντρικών: μόνο τοπικά.
 */
const cacheKey = (identity: string) => `xf_menu_pins:${identity}`;

const readCache = (identity: string | null): string[] | null => {
  if (!identity) return null;
  try {
    const raw = localStorage.getItem(cacheKey(identity));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeCache = (identity: string, pins: string[] | null) => {
  try {
    if (pins) localStorage.setItem(cacheKey(identity), JSON.stringify(pins));
    else localStorage.removeItem(cacheKey(identity));
  } catch { /* ιδιωτική περιήγηση κ.λπ. — απλώς δεν κρατιέται */ }
};

// Μία ανάγνωση από τον server ανά ταυτότητα και φόρτωση σελίδας.
const synced = new Set<string>();

export function useMenuPins(identity: string | null) {
  const [pins, setPinsState] = useState<string[] | null>(() => readCache(identity));

  useEffect(() => {
    setPinsState(readCache(identity));
    if (!identity || synced.has(identity)) return;
    synced.add(identity);
    let alive = true;
    meAPI.getPreferences()
      .then(r => {
        if (r.scope !== 'employee') return;
        const serverPins = Array.isArray(r.preferences?.menu_pins) ? r.preferences.menu_pins as string[] : null;
        writeCache(identity, serverPins);
        if (alive) setPinsState(serverPins);
      })
      .catch(() => { synced.delete(identity); });
    return () => { alive = false; };
  }, [identity]);

  const setPins = useCallback((next: string[] | null) => {
    setPinsState(next);
    if (!identity) return;
    writeCache(identity, next);
    meAPI.saveMenuPins(next).catch(() => { /* μένει τοπικά· ξανασώζεται στην επόμενη αλλαγή */ });
  }, [identity]);

  return { pins, setPins };
}
